import {useCallback, useEffect, useRef, useState} from 'react';
import dayjs from 'dayjs';

import pickedUpDataService, {PickedUpData, uploadFailedPickUpPhotos} from "../../../../services/PickedUpDataService";
import {usePickupTaskOperations} from "../../store/pickupStore";

export interface UsePickedUpFailedRetryResult {
    failedUploadCount: number;
    failedUploads: PickedUpData[];
    isRetrying: boolean;
    showRetryingHint: boolean;
    handleRetry: () => Promise<void>;
}

export const usePickedUpFailedRetry = (): UsePickedUpFailedRetryResult => {
    const [failedUploadCount, setFailedUploadCount] = useState<number>(0);
    const [isRetrying, setIsRetrying] = useState<boolean>(false);
    const [failedUploads, setFailedUploads] = useState<PickedUpData[]>([]);
    const [showRetryingHint, setShowRetryingHint] = useState<boolean>(false);
    const { updatePickupAppointment, createUnloadRecord, handlePickupFailed, markArrivedAtStartingPoint,updateCompletedPickupAppointment} = usePickupTaskOperations();
    const hasInitialRetryRun = useRef<boolean>(false);

    // 获取失败订单列表
    const getFailedPickedUpData = useCallback(async () => {
        try {
            const pickedUpData = await pickedUpDataService.getAllPickedUpData();
            setFailedUploads(pickedUpData);
            return pickedUpData;
        } catch (error) {
            console.error('Failed to fetch pickedUpData:', error);
            return [];
        }
    }, []);

    // 获取失败订单数量
    const getFailedUploadsCount = useCallback(async () => {
        try {
            const count = await pickedUpDataService.getPickedUpDataCount();
            setFailedUploadCount(count);
            return count;
        } catch (error) {
            console.error('Failed to fetch pickedUpData count:', error);
            return 0;
        }
    }, []);

    const handleRetry = useCallback(async () => {
        const currentCount = await getFailedUploadsCount();
        if (currentCount <= 0) {
            setIsRetrying(false);
            setShowRetryingHint(false);
            return;
        }

        const currentUploads = await getFailedPickedUpData();
        if (currentUploads.length === 0) {
            setIsRetrying(false);
            setShowRetryingHint(false);
            return;
        }

        setIsRetrying(true);
        setShowRetryingHint(true);
        let allSuccess = true;

        try {
            for (const pickedUpData of currentUploads) {
                const type = pickedUpData.type;
                try {
                    if (type === 'pickedUp'){
                        await updatePickupAppointment({
                            appointmentId: pickedUpData?.id ||  '0',
                            taskId: pickedUpData.taskId || '0',
                            actualParcelCount: pickedUpData.actualPickupCount || 0,
                            actualParcelUnit: pickedUpData.actualPickupUnit || 1,
                            photosS3KeysJson: await uploadFailedPickUpPhotos(pickedUpData.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo)) || [],
                            actualPickupDate: dayjs().format("YYYY-MM-DD"),
                            actualPickupTimeEnd: dayjs().format("HH:mm"),
                            cooperationWarehouseId: pickedUpData?.cooperationWarehouseId,
                        },false);
                    }else if (type === 'handover'){
                        await createUnloadRecord({
                            taskId: pickedUpData?.taskId || '',
                            warehouseId: pickedUpData?.warehouseId || '',
                            status: pickedUpData?.status || 2,
                            photoS3Keys: await uploadFailedPickUpPhotos(pickedUpData.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo)) || []
                        },false);
                    }else if (type === 'failed') {
                        await handlePickupFailed({
                            appointmentId: pickedUpData?.id || '0',
                            taskId: pickedUpData?.taskId || '',
                            failedReason: pickedUpData?.failedReason || '',
                            exceptionPhotosS3KeysJson: await uploadFailedPickUpPhotos(pickedUpData.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo)) || [],
                            actualPickupDate: dayjs().format("YYYY-MM-DD"),
                            actualPickupTimeEnd: dayjs().format("HH:mm"),
                            cooperationWarehouseId: pickedUpData?.cooperationWarehouseId,
                        }, false);
                    } else if (type === 'arrivedAtStartingPoint') {
                        await markArrivedAtStartingPoint({
                            taskId: pickedUpData?.taskId || '',
                            appointmentId: pickedUpData?.id || '',
                        },false)
                    } else if (type === 'failedToSec') {
                        await updateCompletedPickupAppointment ({
                            appointmentId: pickedUpData?.id || '',
                            taskId: pickedUpData?.taskId || '',
                            actualParcelCount: pickedUpData.actualPickupCount || 0,
                            actualParcelUnit: pickedUpData.actualPickupUnit || 1,
                            photosS3KeysJson: await uploadFailedPickUpPhotos(pickedUpData.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo)) || [],
                            actualPickupDate: dayjs().format("YYYY-MM-DD"),
                            actualPickupTimeEnd: dayjs().format("HH:mm"),
                            cooperationWarehouseId: pickedUpData?.cooperationWarehouseId,
                        },false)
                    }

                    await pickedUpDataService.deletePickedUpData(pickedUpData?.id?.toString());
                } catch (error: any) {
                    console.log('Failed to retry upload:', error);
                    allSuccess = false;
                    if (!error.networkError) {
                        console.log('Non-network error detected, deleting pickedUpData:', error);
                        await pickedUpDataService.deletePickedUpData(pickedUpData?.id?.toString());
                    }
                }
            }

            const remainingCount = await getFailedUploadsCount();

            if (allSuccess && remainingCount === 0) {
                setIsRetrying(false);
                setShowRetryingHint(false);
            } else if (remainingCount === 0) {
                setIsRetrying(false);
                setShowRetryingHint(false);
            }
        } catch (error) {
            console.log('Failed to retry upload:', error);
        } finally {
            setIsRetrying(false);
        }
    }, [getFailedUploadsCount, getFailedPickedUpData, updatePickupAppointment]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;

        const checkFailedUploads = async () => {
            const count = await getFailedUploadsCount();
            if (count > 0 && !isRetrying) {
                await handleRetry();
            } else if (count === 0 && isRetrying) {
                setIsRetrying(false);
                setShowRetryingHint(false);
            }
        };

        // 只在初次进入页面时立即执行一次
        if (!hasInitialRetryRun.current) {
            hasInitialRetryRun.current = true;
            checkFailedUploads();
        }
        
        // 设置定时器，每2分钟检查一次
        intervalId = setInterval(async () => {
            const count = await getFailedUploadsCount();
            if (count > 0 && !isRetrying) {
                await handleRetry();
            }
        }, 1 * 60 * 1000);

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [getFailedUploadsCount, handleRetry, isRetrying]);

    useEffect(() => {
        getFailedPickedUpData();
        getFailedUploadsCount();
    }, [getFailedPickedUpData, getFailedUploadsCount]);

    return {
        failedUploadCount,
        failedUploads,
        isRetrying,
        showRetryingHint,
        handleRetry,
    };
};
